import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';

export default function Projekter() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightboxState, setLightboxState] = useState({ isOpen: false, images: [], index: 0 });

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/projects?limit=100');
        if (res.ok) {
          const data = await res.json();
          setProjects(data.items || []);
          setFilteredProjects(data.items || []);
        }
      } catch (error) {
        console.error("Fejl ved hentning af projekter:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === activeFilter));
    }
  }, [activeFilter, projects]);

  const filters = [
    { id: 'all', label: 'Alle projekter' },
    { id: 'nyt-tag', label: 'Nyt tag' },
    { id: 'terrasser', label: 'Terrasser' },
    { id: 'tilbygninger', label: 'Tilbygninger & renovering' },
    { id: 'doere-vinduer', label: 'Døre & vinduer' }
  ];

  return (
    <>
      <SEO 
        title="Projekter" 
        description="Se billeder og beskrivelser af vores tidligere tømrerprojekter i Egtved, Kolding og Vejle. Vi udfører arbejde i højeste kvalitet." 
      />

      {/* Hero / Header */}
      <section className="bg-brand-wood-light py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex mb-4 text-xs sm:text-sm text-gray-500 gap-2 items-center">
              <Link to="/" className="hover:text-brand-wood">Forside</Link>
              <i className="fa-solid fa-chevron-right text-[10px]" />
              <span className="text-brand-wood-dark font-semibold">Projekter</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark tracking-tight leading-tight">
              Vores tidligere tømrerarbejde
            </h1>
            <p className="mt-4 text-base sm:text-lg text-brand-dark-light leading-relaxed">
              Vi er stolte af det arbejde, vi leverer. Gå på opdagelse i vores galleri af tidligere projekter og lad dig inspirere til dit eget næste byggeprojekt.
            </p>
          </div>
        </div>
      </section>

      {/* Filter og Projekter */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-12 justify-center">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  activeFilter === f.id
                    ? 'bg-brand-green text-white shadow-md'
                    : 'bg-brand-cream text-brand-dark hover:bg-brand-wood-light hover:text-brand-wood-dark border border-gray-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Projekter Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="bg-brand-cream rounded-2xl overflow-hidden border border-gray-100 shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col h-full"
                >
                  {/* Billede */}
                  <div 
                    className="h-64 overflow-hidden relative group cursor-pointer"
                    onClick={() => {
                      if (project.image && project.image.length > 0) {
                        setLightboxState({ isOpen: true, images: project.image, index: 0 });
                      }
                    }}
                  >
                    <img 
                      src={imageUrl(project.image?.[0], 'width=600,quality=85,format=auto')} 
                      alt={project.title?.da} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-brand-green text-white text-xs font-semibold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                      {project.category === 'nyt-tag' && 'Nyt tag'}
                      {project.category === 'terrasser' && 'Terrasse'}
                      {project.category === 'tilbygninger' && 'Tilbygning'}
                      {project.category === 'doere-vinduer' && 'Døre & vinduer'}
                    </div>
                    {project.image && project.image.length > 1 && (
                      <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-xs text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
                        <i className="fa-solid fa-camera text-[10px]" />
                        <span>{project.image.length} billeder</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="bg-white/90 text-brand-dark text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1.5">
                        <i className="fa-solid fa-magnifying-glass-plus" />
                        <span>Se billeder</span>
                      </span>
                    </div>
                  </div>

                  {/* Thumbnail række hvis flere billeder */}
                  {project.image && project.image.length > 1 && (
                    <div className="px-6 pt-3 flex gap-2 overflow-x-auto">
                      {project.image.map((img, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightboxState({ isOpen: true, images: project.image, index: i })}
                          className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <img 
                            src={imageUrl(img, 'width=100,height=100,fit=cover,quality=75,format=auto')} 
                            alt={`${project.title?.da} ${i + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Indhold */}
                  <div className="p-6 sm:p-8 flex flex-col justify-between flex-grow space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-brand-dark mb-3">
                        {project.title?.da}
                      </h3>
                      <div 
                        className="text-brand-dark-light text-sm leading-relaxed space-y-2"
                        dangerouslySetInnerHTML={{ __html: project.description?.da }}
                      />
                    </div>

                    {project.completion_date && (
                      <div className="text-xs text-gray-400 font-medium pt-4 border-t border-gray-100 flex justify-between items-center">
                        <span>Færdiggjort:</span>
                        <span className="font-semibold text-brand-dark">
                          {new Date(project.completion_date).toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-brand-cream rounded-2xl border border-dashed border-gray-200">
              <i className="fa-solid fa-folder-open text-gray-300 text-4xl mb-4 block" />
              <p className="text-gray-500 font-medium">Der blev ikke fundet nogen projekter i denne kategori.</p>
            </div>
          )}
        </div>
      </section>

      {/* Afsluttende CTA */}
      <section className="bg-brand-dark text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/2 left-10 w-72 h-72 rounded-full border-4 border-white" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="text-gray-300 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            Uanset om du skal have lagt nyt tag, bygget en træterrasse eller står overfor en større renovering, står vi klar til at levere håndværk i topklasse.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="bg-brand-green hover:bg-brand-green/90 text-white font-semibold px-6 py-3.5 rounded-xl shadow-md transition-all cursor-pointer text-sm sm:text-base"
            >
              Få et uforpligtende tilbud
            </Link>
            <a 
              href="tel:+4528455012" 
              className="bg-brand-dark-light hover:bg-brand-dark-light/80 text-white border border-gray-700 font-semibold px-6 py-3.5 rounded-xl transition-all flex items-center gap-2 text-sm sm:text-base"
            >
              <i className="fa-solid fa-phone text-brand-wood" />
              <span>Ring: 28 45 50 12</span>
            </a>
          </div>
        </div>
      </section>
      {/* Lightbox til visning af billeder */}
      {lightboxState.isOpen && (
        <Lightbox 
          images={lightboxState.images}
          currentIndex={lightboxState.index}
          onClose={() => setLightboxState({ isOpen: false, images: [], index: 0 })}
          onPrev={() => setLightboxState(prev => ({
            ...prev,
            index: (prev.index - 1 + prev.images.length) % prev.images.length
          }))}
          onNext={() => setLightboxState(prev => ({
            ...prev,
            index: (prev.index + 1) % prev.images.length
          }))}
        />
      )}
    </>
  );
}
